import React, { FC, useMemo, useState, useCallback, useRef, useEffect } from 'react'
import cls from 'clsx'
import { TextFieldProps, TextField } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Controller, useFormContext } from 'react-hook-form'
import { ListEmailDomainsEnum } from '@sparling/financial-common'
import capitalizeFirstLetter from '../../../utils/capitalizeFirstLetter'
import { errorMessage as errorText } from '../../../constants/errorMessages'

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    width: '300px',
    height: '48px',
    marginBottom: theme.spacing(3),
    '&:hover .MuiInput-underline:before ': {
      borderBottom: `1px solid ${theme.palette.primary.main}`,
    },
    '& .MuiInput-underline:after ': {
      borderBottom: `1px solid ${theme.palette.info.light}`,
    },
    '& .MuiInputBase-input': {
      fontSize: 14,
      fontWeight: 500,
    },
    '& label.Mui-error': {
      color: '#3E3E3E',
    },
    '& .Mui-error': {
      lineHeight: '1.2',
    },
  },
  inputHidden: {
    display: 'none',
    visibility: 'hidden',
    position: 'absolute',
    clip: 'rect(0 0 0 0)',
    width: 1,
    height: 1,
    margin: '-1px',
  },
}))

interface IFormTextField {
  props?: TextFieldProps
  name: string
  label: string
  isCompanyEmail?: boolean
  defaultValue?: string
  disabled?: boolean
  type?: string
}

const smartSearch = (domain: string, text: string): boolean => {
  const result = text.toLocaleLowerCase().replace(/^[A-Za-z0-9._%+-]+@/, '')
  return result === domain
}

const popularEmailDomainsValidator = (value: string) => {
  if (!value) return true
  if (/@/.test(value)) {
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const domain in ListEmailDomainsEnum) {
      if (smartSearch(ListEmailDomainsEnum[domain as keyof typeof ListEmailDomainsEnum], value)) {
        return true
      }
    }
    return false
  }
  return true
}

export const CInputField: FC<IFormTextField> = ({
  props,
  name,
  label,
  isCompanyEmail = false,
  defaultValue = '',
  disabled,
  type,
}) => {
  const classes = useStyles()
  const [isCompanyEmailError, setIsCompanyEmailError] = useState<boolean>(false)
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const error = errors[name]
  const inputEl = useRef<HTMLInputElement>(null)

  const errorMessage = useMemo(() => {
    const message = error?.message?.replace(name, '') ?? ''
    return capitalizeFirstLetter(message)
  }, [error, name])

  const emailValidator = useCallback(
    // eslint-disable-next-line
    ($event: any) => {
      if (isCompanyEmail) {
        const {
          target: { value },
        } = $event
        setIsCompanyEmailError(value.length ? popularEmailDomainsValidator(value) : false)
      }
    },
    [isCompanyEmail],
  )

  useEffect(() => {
    if (isCompanyEmail) {
      inputEl?.current?.addEventListener('blur', ($event) => emailValidator($event))
      if (inputEl?.current?.value.length) {
        inputEl?.current?.focus()
        inputEl?.current?.blur()
      }
    }
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (isCompanyEmail) inputEl?.current?.removeEventListener('blur', ($event) => emailValidator($event))
    }
  }, [emailValidator, isCompanyEmail])

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      rules={{ required: true }}
      render={({ field }) => (
        <TextField
          {...field}
          {...props}
          type={type}
          variant={'standard'}
          inputRef={inputEl}
          error={!!error || (!error && isCompanyEmailError && isCompanyEmail)}
          label={label}
          disabled={disabled}
          className={cls(classes.input, type === 'hidden' ? classes.inputHidden : undefined)}
          helperText={
            !error && isCompanyEmailError && isCompanyEmail ? errorText.RECOMMENDATION_CORPORATE_MAIL : errorMessage
          }
        />
      )}
    />
  )
}
